/**
 * How old a reading is, and when to stop trusting it.
 *
 * A monitoring dashboard that renders a cached snapshot indefinitely is
 * asserting something it does not know: "[OK] NOW" stays on screen whether or
 * not anything is still polling, because both the badge and the relative time
 * were computed once at page load. The failure is the same shape as a
 * fabricated data point -- the screen states more than the data supports.
 *
 * So age is computed against a clock the caller passes in (which is what makes
 * it testable, and what lets a ticking clock decay the label between fetches),
 * and a reading older than several poll intervals is reported as stale rather
 * than as whatever the last poll happened to say.
 */

/** Missed polls tolerated before a reading is called stale. */
const STALE_AFTER_POLLS = 3;

/**
 * Floor on the staleness threshold. A device polled every 10s would otherwise
 * be called stale after 30s, which is close enough to normal jitter -- one
 * slow SNMP round trip -- to cry wolf.
 */
const STALE_FLOOR_MS = 120_000;

export function stalenessThresholdMs(pollIntervalSeconds: number): number {
  return Math.max(pollIntervalSeconds * 1000 * STALE_AFTER_POLLS, STALE_FLOOR_MS);
}

/** Age in ms, or null when the timestamp is absent or unparseable. */
export function ageMs(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  // Clamp: a clock a few seconds apart from the server's must not render as
  // a negative age.
  return Math.max(0, now - parsed);
}

export function isPollStale(
  lastPollAt: string | null,
  pollIntervalSeconds: number,
  now: number,
): boolean {
  const age = ageMs(lastPollAt, now);
  if (age === null) return false; // Never polled is its own state, not staleness.
  return age > stalenessThresholdMs(pollIntervalSeconds);
}

/**
 * What the status badge should actually say.
 *
 * Staleness outranks the recorded status deliberately. If the poller dies, the
 * last row it wrote says "ok" forever; showing that green is how a dead fleet
 * looks healthy. "How old is this" is the more important fact once the reading
 * has aged out, so it wins -- the underlying error text is still displayed
 * alongside it on the detail page.
 */
/** Whether a maintenance window is open, judged against the live clock. */
export function inMaintenance(
  device: { maintenance_started_at?: string | null; maintenance_until?: string | null },
  now: number,
): boolean {
  if (!device.maintenance_started_at) return false;
  return !device.maintenance_until || Date.parse(device.maintenance_until) > now;
}

export function displayStatus(
  device: {
    last_poll_at: string | null;
    last_poll_status: string;
    poll_interval_seconds: number;
    enabled?: boolean;
    maintenance_started_at?: string | null;
    maintenance_until?: string | null;
  },
  now: number,
): string {
  // Switched off outranks everything, maintenance included. A disabled device
  // is not polled, so its last reading ages until it reads as STALE -- which
  // looks like a fault, and a bench unit sat looking broken for fourteen hours
  // that way when it had simply been turned off. MAINT would be wrong too:
  // maintenance means "still polled, alerting nobody", and this is neither.
  if (device.enabled === false) return "off";
  // Maintenance outranks everything, staleness included. During planned work
  // the device is expected to drop and reboot, and what matters at a glance
  // is "someone is working on this, and nobody is being alerted" -- the one
  // fact that changes how to read every other number on the page. Judged on
  // the clock rather than a server flag, so the badge falls back to the real
  // status the moment the window ends, without waiting for a refetch.
  if (inMaintenance(device, now)) return "maint";
  if (!device.last_poll_at) return "never";
  if (isPollStale(device.last_poll_at, device.poll_interval_seconds, now)) return "stale";
  return device.last_poll_status;
}

/**
 * Age label. Recomputed from `now`, so it decays without a refetch.
 *
 * Seconds are shown rather than a "NOW" band, and that is the whole point of
 * the first branch. Devices poll every 60s by default, so any threshold above
 * that makes a healthy device read the same word permanently -- which is
 * indistinguishable from a page frozen at load, the exact thing the refresh
 * was added to rule out. A number that counts up and resets each poll cycle
 * says both "this is current" and "the page is live".
 */
export function relativeTime(iso: string | null, now: number): string {
  const age = ageMs(iso, now);
  if (age === null) return "never";

  const seconds = age / 1000;
  if (seconds < 90) return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}
